const STALE_ASSET_RELOAD_KEY_PREFIX = 'hector:stale-asset-reload:';

const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \S+ failed/i,
  /Expected a JavaScript-or-Wasm module script/i,
  /Strict MIME type checking is enforced for module scripts/i,
  // Vite preloads a chunk's stylesheet first: a removed `.css` fails before its `.js`.
  /Unable to preload CSS/i,
];

const ASSET_URL_PATTERN = /(?:https?:\/\/[^\s"'<>]+|\/assets\/[^\s"'<>]+)\.(?:js|mjs)(?:\?[^\s"'<>]*)?/i;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

interface ReloadOnceOptions {
  storage?: StorageLike | null;
  reload?: () => void;
}

function textFromUnknown(value: unknown): string {
  if (value instanceof Error) {
    return [value.name, value.message, value.stack].filter(Boolean).join('\n');
  }
  if (typeof value === 'string') return value;
  if (typeof Event !== 'undefined' && value instanceof Event) {
    const target = value.target;
    const isScriptElement =
      typeof HTMLScriptElement !== 'undefined' && target instanceof HTMLScriptElement;
    const isLinkElement =
      typeof HTMLLinkElement !== 'undefined' && target instanceof HTMLLinkElement;
    const src = isScriptElement
      ? target.src
      : isLinkElement
        ? target.href
        : '';
    return [value.type, src].filter(Boolean).join('\n');
  }
  if (typeof value === 'object' && value !== null) {
    const maybeMessage = 'message' in value ? value.message : undefined;
    const maybeSrc = 'src' in value ? value.src : undefined;
    const maybeHref = 'href' in value ? value.href : undefined;
    return [maybeMessage, maybeSrc, maybeHref]
      .filter((part): part is string => typeof part === 'string')
      .join('\n');
  }
  return '';
}

function storageFromBrowser(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function reloadFromBrowser(): (() => void) | null {
  if (typeof window === 'undefined') return null;
  return () => window.location.reload();
}

export function staleAssetReloadSignature(error: unknown): string | null {
  const text = textFromUnknown(error);
  if (!CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(text))) return null;

  const assetUrl = text.match(ASSET_URL_PATTERN)?.[0];
  return assetUrl ?? text.slice(0, 200);
}

export function isStaleAssetLoadError(error: unknown): boolean {
  return staleAssetReloadSignature(error) !== null;
}

/**
 * Vite route chunks are content-hashed. After a deploy, an already-open SPA can
 * still request a chunk from the previous build; Vercel rewrites that missing
 * asset to index.html, so React sees a dynamic-import/MIME failure. A single
 * hard reload fetches the new index and chunk graph.
 */
export function reloadOnceForStaleAssetLoad(error: unknown, options: ReloadOnceOptions = {}): boolean {
  const signature = staleAssetReloadSignature(error);
  if (!signature) return false;

  const storage = options.storage === undefined ? storageFromBrowser() : options.storage;
  const reload = options.reload ?? reloadFromBrowser();
  if (!storage || !reload) return false;

  const reloadKey = `${STALE_ASSET_RELOAD_KEY_PREFIX}${signature}`;
  if (storage.getItem(reloadKey) === '1') return false;

  storage.setItem(reloadKey, '1');
  reload();
  return true;
}

export function installStaleAssetReloadHandler(): () => void {
  if (typeof window === 'undefined') return () => {};

  const onPreloadError = (event: Event) => {
    const payload = 'payload' in event ? (event as Event & { payload?: unknown }).payload : event;
    if (reloadOnceForStaleAssetLoad(payload)) {
      event.preventDefault();
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
